import { Loader2, Mic, MicOff, Square } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { dictationMessage } from "@/components/ui/DictationButton";
import { DictationMicMenu } from "@/components/ui/DictationButton/DictationMicMenu";
import { Textarea } from "@/components/ui/textarea";
import {
  DICTATION_MAX_SECONDS,
  useDictation,
} from "@/hooks/transcriber/useDictation";
import { cn } from "@/lib/utils";

const formatSeconds = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

type Props = {
  id: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onDictatingChange?: (dictating: boolean) => void;
  // Extra controls in the bottom-left corner of the box (e.g. attach file).
  accessory?: ReactNode;
  // A shorter box, e.g. while a proposal for this instruction is shown.
  compact?: boolean;
  // A button on the status line's row from `sm` up (under it on phones);
  // inside the box, next to the mic, when compact.
  action?: ReactNode;
  // Between the box and the status line (e.g. attached-file chips).
  attachments?: ReactNode;
};

// A textarea with a mic button: dictated words (through the transcriber
// socket) are appended to whatever was typed before pressing the mic.
// The status line always keeps its height so the layout never jumps.
export const DictationTextarea = ({
  id,
  value,
  placeholder,
  onChange,
  onDictatingChange,
  accessory,
  compact = false,
  action,
  attachments,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const dictation = useDictation();
  const typedBeforeDictation = useRef("");
  const { status, error, elapsedSeconds } = dictation;
  const isDictating = status !== "idle";
  const isBusy = status === "connecting" || status === "finishing";

  useEffect(() => {
    if (dictation.text) {
      onChange(`${typedBeforeDictation.current} ${dictation.text}`.trim());
    }
  }, [dictation.text]);

  useEffect(() => {
    onDictatingChange?.(isDictating);
  }, [isDictating]);

  const toggle = (): void => {
    if (isDictating) {
      dictation.stop();
      return;
    }
    typedBeforeDictation.current = value;
    void dictation.start();
  };

  const label = t(isDictating ? "dictation.stop" : "dictation.start");
  const maxTime = formatSeconds(DICTATION_MAX_SECONDS);
  const statusText =
    dictationMessage(t, { status, error, elapsedSeconds }) ??
    t("dictation.hint", { max: maxTime });

  // A compact box drops the idle hint; dictation and errors still show.
  const showStatus = !compact || status !== "idle" || !!error;
  const statusLine = (
    <p
      aria-live="polite"
      className={cn(
        "flex min-h-5 items-center gap-1.5 text-xs text-muted-foreground",
        error && !isDictating && "text-destructive",
      )}
    >
      {status === "listening" && (
        <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-destructive" />
      )}
      <span className="min-w-0">{statusText}</span>
      {status === "listening" && (
        <span
          aria-hidden
          className="ml-auto shrink-0 pl-2 tabular-nums"
          data-testid="dictation-elapsed"
        >
          {formatSeconds(elapsedSeconds)} / {maxTime}
        </span>
      )}
    </p>
  );

  return (
    <div className="space-y-1.5">
      <div
        className={cn(
          "relative rounded-md transition-shadow",
          status === "listening" && "ring-2 ring-destructive/40",
        )}
      >
        <Textarea
          className={cn(
            "resize-y pb-14 text-base transition-[min-height]",
            compact ? "min-h-[88px]" : "min-h-[120px]",
            accessory ? "sm:pr-3" : "sm:pb-3 sm:pr-20",
          )}
          id={id}
          placeholder={placeholder}
          readOnly={isDictating}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
        {accessory && (
          <div className="absolute bottom-2 left-2 flex items-center">
            {accessory}
          </div>
        )}
        {/* Compact: the action moves into the box, next to the mic. */}
        {compact && action && (
          <div className="absolute bottom-2 right-[4.75rem] flex items-center [&_button]:w-auto">
            {action}
          </div>
        )}
        <DictationMicMenu
          className="absolute bottom-3 right-[3.25rem]"
          disabled={isDictating}
        />
        <Button
          aria-label={label}
          aria-pressed={isDictating}
          disabled={status === "finishing"}
          size="icon"
          title={label}
          type="button"
          variant={isDictating ? "destructive" : "outline"}
          className={cn(
            "absolute bottom-2 right-2 h-11 w-11 rounded-full shadow-xs",
            status === "listening" && "animate-pulse",
          )}
          onClick={toggle}
        >
          {isBusy && <Loader2 className="animate-spin" />}
          {status === "listening" && <Square className="fill-current" />}
          {status === "idle" &&
            (error ? <MicOff className="text-destructive" /> : <Mic />)}
        </Button>
      </div>
      {attachments}
      {action && !compact ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {showStatus && statusLine}
          <div className="flex shrink-0 justify-end sm:ml-auto">{action}</div>
        </div>
      ) : (
        showStatus && statusLine
      )}
    </div>
  );
};
